import { Api } from '../constants/Api';

const NO_TOKEN_ENDPOINTS = [
  'signup',
  'signin',
  'user/signup',
  'user/signin',
  'user/resetPassword',
  'user/confirmResetPassword',
  'game_public/game_version_news',
  'game_public/game_version_news_latest',
];

export class HttpService {
  private token: string = '';

  setToken(token: string): void {
    this.token = token;
  }

  private getToken(): string | null {
    // Always get the latest token from localStorage to ensure it's up to date
    if (typeof window !== 'undefined') {
      return localStorage.getItem('token');
    }
    return this.token || null;
  }

  private getHeaders(endpoint: string): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();

    if (!this.isNoTokenEndpoint(endpoint) && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private isNoTokenEndpoint(endpoint: string): boolean {
    return NO_TOKEN_ENDPOINTS.some(
      (noTokenEndpoint) =>
        endpoint === noTokenEndpoint || endpoint.startsWith(noTokenEndpoint)
    );
  }

  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${Api.SERVER_URL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(endpoint),
    });

    if (!response.ok) {
      if (response.status === 401 && !this.isNoTokenEndpoint(endpoint)) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        }
        const error = await response.json().catch(() => ({ message: 'Your session has expired. Please login again.' }));
        throw new Error(error.message || 'Your session has expired. Please login again.');
      }
      const error = await response.json().catch(() => ({}));
      const message = (error && (error.message || error.error)) || 'Request failed';
      throw new Error(typeof message === 'string' ? message : 'Request failed');
    }

    return response.json();
  }

  async post<T>(endpoint: string, body: any): Promise<T> {
    const response = await fetch(`${Api.SERVER_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(endpoint),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401 && !this.isNoTokenEndpoint(endpoint)) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        }
        const error = await response.json().catch(() => ({ message: 'Your session has expired. Please login again.' }));
        throw new Error(error.message || 'Your session has expired. Please login again.');
      }
      const error = await response.json().catch(() => ({}));
      const message = (error && (error.message || error.error)) || 'Request failed';
      throw new Error(typeof message === 'string' ? message : 'Request failed');
    }

    return response.json();
  }

  async put<T>(endpoint: string, body: any): Promise<T> {
    const response = await fetch(`${Api.SERVER_URL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(endpoint),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401 && !this.isNoTokenEndpoint(endpoint)) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        }
        const error = await response.json().catch(() => ({ message: 'Your session has expired. Please login again.' }));
        throw new Error(error.message || 'Your session has expired. Please login again.');
      }
      const error = await response.json().catch(() => ({}));
      const message = (error && (error.message || error.error)) || 'Request failed';
      throw new Error(typeof message === 'string' ? message : 'Request failed');
    }

    return response.json();
  }

  async patch<T>(endpoint: string, body: any): Promise<T> {
    const response = await fetch(`${Api.SERVER_URL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(endpoint),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401 && !this.isNoTokenEndpoint(endpoint)) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        }
        const error = await response.json().catch(() => ({ message: 'Your session has expired. Please login again.' }));
        throw new Error(error.message || 'Your session has expired. Please login again.');
      }
      const error = await response.json().catch(() => ({}));
      const message = (error && (error.message || error.error)) || 'Request failed';
      throw new Error(typeof message === 'string' ? message : 'Request failed');
    }

    return response.json();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${Api.SERVER_URL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(endpoint),
    });

    if (!response.ok) {
      if (response.status === 401 && !this.isNoTokenEndpoint(endpoint)) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('emailVerified');
          window.location.href = '/login';
        }
        const error = await response.json().catch(() => ({ message: 'Your session has expired. Please login again.' }));
        throw new Error(error.message || 'Your session has expired. Please login again.');
      }
      const error = await response.json().catch(() => ({}));
      const message = (error && (error.message || error.error)) || 'Request failed';
      throw new Error(typeof message === 'string' ? message : 'Request failed');
    }

    return response.json();
  }
}

